import React, { useContext, useMemo } from "react";
import { Table, Button, Select, Switch, Input, Popconfirm, Tag } from "antd";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { showIssues } from "../../helper/notify";
import {
  DeleteOutlined,
  EyeOutlined,
  HolderOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SelectWithAdd from "../../components/generic/SelectWithAdd";
import { labelForCategory } from "../../constants/reconciliationLabels";
import "../../styles/custom-dropdown.css";
import "../../styles/custom-switch.css";

const { Option } = Select;
const RowContext = React.createContext({});

const Row = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: props["data-row-key"] });
  const style = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };
  const contextValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );
  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "move" }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

// Presentational step grid. All state + handlers live in the shared
// useMasterSuiteStepEditor hook (passed in as `editor`), so this surface — and
// later its removal — never touches the step logic.
function MasterSuiteNewTable({ editor }) {
  const {
    dataSource,
    errorFields,
    allCategories,
    handleAdd,
    handleDelete,
    onDragEnd,
    handleTypeChange,
    handleCategoryChange,
    handleSubcategoryChange,
    handleStepReferenceChange,
    handleTaskNameChange,
    handleSwitchChange,
    handleVariablesClick,
    handleAddTaskItem,
    onClickViewTask,
  } = editor;

  const onAddStep = () => {
    const result = handleAdd();
    if (result?.error) showIssues("Can't add a step yet", result.error);
  };

  const columns = [
    {
      key: "sort",
      align: "center",
      render: () => <DragHandle />,
      width: "4%",
    },
    {
      title: "",
      dataIndex: "add",
      key: "add",
      width: "4%",
      render: (_, record) => (
        <Popconfirm
          title="Are you sure?"
          onConfirm={() => handleDelete(record.key)}
        >
          <DeleteOutlined style={{ color: "red" }} />
        </Popconfirm>
      ),
    },
    {
      title: "#",
      dataIndex: "sequence",
      key: "sequence",
      width: "3%",
      align: "center",
      render: (_, record) => record.sequence,
    },
    {
      title: "Step Name",
      dataIndex: "stepReference",
      key: "stepReference",
      render: (stepReference, record) => {
        const hasError = errorFields[record.key]?.stepReference;
        return (
          <Input
            value={stepReference}
            style={{
              fontSize: "12px",
              borderColor: hasError ? "red" : undefined,
            }}
            status={hasError ? "error" : undefined}
            onChange={(e) => handleStepReferenceChange(e.target.value, record)}
            placeholder={hasError || "Enter step name"}
            maxLength={50}
          />
        );
      },
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      responsive: ["md"],
      width: "10%",
      render: (_, record) => (
        <Select
          style={{
            width: "100%",
            borderColor: errorFields[record.key]?.type ? "red" : undefined,
          }}
          className="custom-select"
          onChange={(value) => handleTypeChange(value, record)}
          value={record.type}
          placeholder="Type"
        >
          <Option value="Master Suite">Master Suite</Option>
          <Option value="Task">Task</Option>
        </Select>
      ),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: "14%",
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          className="custom-select"
          onChange={(value) => handleCategoryChange(value, record)}
          value={record.type === "Master Suite" ? "N/A" : record.category}
          placeholder="Category"
          // Display-only rename ("Compare" → "Reconciliation"); onChange stores the
          // option value, so the persisted category is unchanged.
          options={allCategories.map((c) => ({
            ...c,
            label: labelForCategory(c.label),
          }))}
          disabled={!record.type || record.type === "Master Suite"}
        />
      ),
    },
    {
      title: "Sub Category",
      dataIndex: "subcategory",
      key: "subcategory",
      responsive: ["lg"],
      width: "14%",
      render: (_, record) => {
        if (record.category === "ui") {
          return (
            <Tag color="purple" style={{ margin: 0 }}>
              UI Test
            </Tag>
          );
        }
        return (
          <Select
            style={{ width: "100%" }}
            className="custom-select"
            value={record.type === "Master Suite" ? "N/A" : record.subcategory}
            onChange={(value) => handleSubcategoryChange(value, record)}
            placeholder="Sub Category"
            options={record.subCategoryOptions || []}
            disabled={!record.category || record.category === "N/A"}
          />
        );
      },
    },
    {
      title: "Task / Suite Name",
      dataIndex: "taskName",
      key: "taskName",
      width: "20%",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <SelectWithAdd
            placeholder={
              record.category === "ui" ? "select UI suite" : "task name"
            }
            options={record.filteredTaskArray || []}
            value={record.taskName || undefined}
            onSelect={(value) => handleTaskNameChange(value, record)}
            labelKey="taskName"
            valueKey="taskName"
            addItem={() => handleAddTaskItem(record)}
            buttonDisabled={
              !record.type ||
              record.type === "Master Suite" ||
              record.category === "ui"
            }
            disabled={
              !record.subcategory ||
              (record.category !== "ui" && !record.subcategory)
            }
          />
          {record.category !== "ui" && (
            <Button type="link" onClick={() => onClickViewTask(record)}>
              <EyeOutlined style={{ fontSize: "24px", color: "gray" }} />
            </Button>
          )}
        </div>
      ),
    },
    {
      title: "Parameters",
      dataIndex: "variables",
      key: "variables",
      align: "center",
      responsive: ["md"],
      width: "10%",
      render: (_, record) => {
        if (record.category === "ui") {
          return <span style={{ color: "#bbb", fontSize: 12 }}>—</span>;
        }
        return (
          <Button
            size="small"
            style={{ fontSize: "12px" }}
            type="link"
            onClick={() => handleVariablesClick(record)}
          >
            {record.variables && Object.keys(record.variables).length > 0
              ? "Edit"
              : "Add"}
          </Button>
        );
      },
    },
    {
      title: "Stop if Failed",
      dataIndex: "stopIfFailed",
      key: "stopIfFailed",
      responsive: ["md"],
      width: "10%",
      align: "center",
      render: (_, record) => (
        <Switch
          className="custom-switch"
          checked={record.stopIfFailed}
          onChange={(checked) => handleSwitchChange(checked, record)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      ),
    },
  ];

  return (
    <>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            className="custom-table-shadow"
            columns={columns}
            dataSource={dataSource}
            rowKey="key"
            components={{ body: { row: Row } }}
            scroll={{ y: 400 }}
            pagination={false}
          />
        </SortableContext>
      </DndContext>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={onAddStep}
        className="db-add"
        style={{ marginTop: 16, float: "left" }}
      >
        Add Step
      </Button>
    </>
  );
}

export default MasterSuiteNewTable;
